import { lazy, ComponentType } from "react";

/**
 * Wraps a dynamic import with retry logic for stale chunk errors.
 * After a new deployment, the browser may have cached old HTML pointing
 * to JS chunks that no longer exist. This detects the failure and
 * reloads the page once to pick up the new HTML + chunks.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  name?: string
) {
  return lazy(() =>
    factory().catch((error: Error) => {
      const key = `chunk-retry-${name || "page"}`;
      const hasRetried = sessionStorage.getItem(key);

      if (!hasRetried) {
        console.warn(`[lazyRetry] Chunk load failed for ${name}, reloading...`, error);
        sessionStorage.setItem(key, "1");
        window.location.reload();
        // Return a never-resolving promise to prevent rendering while reload happens
        return new Promise<{ default: T }>(() => {});
      }

      // Already retried once, clear flag and let the error propagate
      sessionStorage.removeItem(key);
      throw error;
    })
  );
}
